import { useEffect, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeftIcon, CheckCircleIcon, PaperClipIcon } from '@heroicons/react/24/outline';
import { useAuth } from '@/contexts/AuthContext';
import { SI_PORTAL_BASE_PATH } from '../../siPortal.definitions';
import {
  categoriaPublicacao,
  cienciaCobradaDe,
  cienciaEmDia,
  millis,
  situacaoPrazo,
} from '../publicacao.definitions';
import { useMeusEstadosQuery, usePublicacaoLeitorMutations, usePublicacaoQuery } from '../usePublicacoes';
import { PublicacaoCapa } from '../components/PublicacaoCapa';
import { MarkdownPublicacao } from '../components/MarkdownPublicacao';
import { ComentariosPublicacao } from '../components/ComentariosPublicacao';
import { DocumentoRelacionadoNoPost } from '../components/DocumentoRelacionadoNoPost';
import { DestaqueAviso } from '../../documentos/DocumentoCard';
import { useAnexosAbertos } from '../anexosAbertos';

/**
 * Post aberto (rota /si/inicio/publicacao/:id — a aba Início continua marcada). Abrir marca
 * como lida; "Li e estou ciente" só aparece se a ciência é cobrada da pessoa.
 */
export default function PublicacaoPage() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const agora = useMemo(() => Date.now(), []);
  const { data: publicacao, isLoading } = usePublicacaoQuery(id);
  const lista = useMemo(() => (publicacao ? [publicacao] : []), [publicacao]);
  const { estados, isLoading: carregandoEstado } = useMeusEstadosQuery(lista);
  const { marcarLida, confirmarCiencia, curtir } = usePublicacaoLeitorMutations(id ?? '');
  const estado = publicacao ? estados.get(publicacao.id) : undefined;
  // Com anexos, "Li e estou ciente" só depois de abrir todos (lembrado nesta sessão do navegador).
  const { abertos, marcar: abrirAnexo } = useAnexosAbertos(id);

  const publicada = publicacao?.status === 'publicada';
  const { mutate: marcar } = marcarLida;
  useEffect(() => {
    if (publicada && !carregandoEstado && estado && !estado.lida) marcar();
  }, [publicada, carregandoEstado, estado, marcar]);

  const voltar = (
    <Link to={`${SI_PORTAL_BASE_PATH}/inicio`} className="inline-flex items-center gap-2 text-sm font-semibold text-primary-700">
      <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" /> Voltar para o Início
    </Link>
  );

  if (isLoading) return <div className="mx-auto h-96 max-w-3xl animate-pulse rounded-2xl bg-raro-gray-100" />;
  if (!publicacao) {
    return (
      <div className="mx-auto max-w-3xl space-y-4">
        {voltar}
        <p className="rounded-2xl border border-dashed border-raro-gray-300 bg-white px-4 py-6 text-center text-sm text-raro-gray-500">
          Publicação não encontrada.
        </p>
      </div>
    );
  }

  const categoria = categoriaPublicacao(publicacao.categoria);
  const data = millis(publicacao.publicarEm);
  const cobrada = publicada && cienciaCobradaDe(publicacao, user?.collaboratorId);
  const ciente = cienciaEmDia(publicacao, estado);
  const situacao = situacaoPrazo(publicacao, agora);
  const prazo = millis(publicacao.ciencia?.prazo);
  const faltamAnexos = publicacao.anexos.filter((a) => !abertos.includes(a.path)).length;

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      {voltar}
      {!publicada && (
        <p className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-800">
          Pré-visualização — esta publicação ainda não está visível para os colaboradores.
        </p>
      )}

      <article className="overflow-hidden rounded-2xl border border-raro-gray-200 bg-white shadow-sm">
        <PublicacaoCapa titulo={publicacao.titulo} categoria={publicacao.categoria} capaUrl={publicacao.capaUrl} tamanho="pagina" />
        <div className="space-y-4 p-6">
          <p className="text-xs">
            <span className={`font-semibold ${categoria.texto}`}>{categoria.label}</span>
            <span className="text-raro-gray-500">
              {data ? ` · ${new Date(data).toLocaleDateString('pt-BR')}` : ''} · por {publicacao.autor?.name ?? 'Time de SI'}
            </span>
          </p>
          <h2 className="font-montserrat text-2xl font-bold text-raro-gray-900">{publicacao.titulo}</h2>
          <MarkdownPublicacao conteudo={publicacao.conteudo} />

          {publicacao.anexos.length > 0 && (
            <ul className="space-y-2">
              {publicacao.anexos.map((anexo) => (
                <li key={anexo.path}>
                  <a
                    href={anexo.url}
                    target="_blank"
                    rel="noreferrer"
                    onClick={() => abrirAnexo(anexo.path)}
                    className="flex items-center gap-3 rounded-xl border border-raro-gray-200 px-4 py-3 text-sm hover:border-primary-200"
                  >
                    <PaperClipIcon className="h-4 w-4 text-raro-gray-400" aria-hidden="true" />
                    <span className="flex-1 truncate">{anexo.nome}</span>
                    {abertos.includes(anexo.path) ? (
                      <span className="text-xs font-semibold text-green-700">Aberto ✓</span>
                    ) : (
                      <span className="text-xs font-semibold text-primary-700">Abrir</span>
                    )}
                  </a>
                </li>
              ))}
            </ul>
          )}

          {publicacao.documentoRelacionadoId && (
            <DocumentoRelacionadoNoPost documentoId={publicacao.documentoRelacionadoId} colaboradorId={user?.collaboratorId} />
          )}

          {cobrada && (
            <div
              className={`flex flex-col gap-3 rounded-2xl border px-5 py-4 sm:flex-row sm:items-center sm:justify-between ${
                ciente ? 'border-green-200 bg-green-50' : 'border-amber-200 bg-amber-50'
              }`}
            >
              <div>
                <p className={`text-sm font-semibold ${ciente ? 'text-green-900' : 'text-amber-900'}`}>
                  {ciente ? 'Você confirmou a ciência desta publicação' : 'Esta publicação pede sua ciência'}
                </p>
                {!ciente && faltamAnexos > 0 && (
                  <p className="mt-1 text-xs leading-relaxed text-amber-900">
                    ⚠️ <DestaqueAviso>ABRA</DestaqueAviso> e <DestaqueAviso>LEIA</DestaqueAviso>{' '}
                    {publicacao.anexos.length === 1 ? 'o anexo' : `os ${publicacao.anexos.length} anexos`} acima antes de clicar em
                    &quot;Li e estou ciente&quot;
                    {faltamAnexos < publicacao.anexos.length ? ` (falta${faltamAnexos > 1 ? 'm' : ''} ${faltamAnexos})` : ''}.
                  </p>
                )}
                {!ciente && (
                  <p className={`text-xs ${situacao.tipo === 'atrasada' ? 'font-semibold text-rose-700' : 'text-amber-800'}`}>
                    {prazo
                      ? `${situacao.tipo === 'atrasada' ? 'Prazo vencido em' : 'Prazo:'} ${new Date(prazo).toLocaleDateString('pt-BR')}`
                      : 'Sem prazo'}
                  </p>
                )}
              </div>
              {ciente ? (
                <CheckCircleIcon className="h-7 w-7 text-green-600" aria-hidden="true" />
              ) : (
                <button
                  type="button"
                  className="rounded-full bg-primary-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:bg-raro-gray-300"
                  disabled={confirmarCiencia.isPending || carregandoEstado || faltamAnexos > 0}
                  title={faltamAnexos > 0 ? 'Abra os anexos antes de confirmar' : undefined}
                  onClick={() => confirmarCiencia.mutate(publicacao.ciencia.versao)}
                >
                  {confirmarCiencia.isPending ? 'Confirmando...' : 'Li e estou ciente'}
                </button>
              )}
            </div>
          )}

          {publicada && (
            <div className="flex items-center gap-4 border-t border-raro-gray-100 pt-4 text-sm">
              <button
                type="button"
                disabled={curtir.isPending || carregandoEstado}
                onClick={() => curtir.mutate(!estado?.curtiu)}
                aria-pressed={Boolean(estado?.curtiu)}
                className={`rounded-full border px-4 py-1.5 font-medium transition-colors ${
                  estado?.curtiu ? 'border-rose-200 bg-rose-50 text-rose-600' : 'border-raro-gray-300 text-raro-gray-700 hover:border-rose-200'
                }`}
              >
                {estado?.curtiu ? '♥ Curtido' : '♡ Curtir'} · {publicacao.curtidasTotal ?? 0}
              </button>
            </div>
          )}
        </div>
      </article>

      {publicada && <ComentariosPublicacao publicacaoId={publicacao.id} />}
    </div>
  );
}
